import React, { useCallback, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import './index.css';

// Public
import Website, {
  BuyersPage, VendorsPage, PricingPage, BulkSalesPage,
  ServicesPage, BlogPage, AboutPage, ContactPage, VendorSignupPage,
  PAGE_TO_PATH, MarketingFonts,
} from './pages/Website';
import Catalog from './pages/Catalog';
import BulkListings from './pages/BulkListings';
import Login from './pages/Login';
import VendorLogin from './pages/VendorLogin';
import AdminLogin from './pages/AdminLogin';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ItemDetail from './pages/ItemDetail';
import CategoryPage from './pages/CategoryPage';
import PublicLayout from './components/PublicLayout';
import Seo from './components/Seo';
import Notifications from './pages/Notifications';
import Security from './pages/Security';
import RecentDemands from './pages/RecentDemands';
import LegalPage from './pages/legal/LegalPage';
import Messages from './pages/Messages';
import AppDownload from './pages/AppDownload';

// Layout
import AppLayout from './components/AppLayout';

// Buyer
import BuyerHome from './pages/buyer/BuyerHome';
import PostDemand from './pages/buyer/PostDemand';
import MyDemands from './pages/buyer/MyDemands';
import DemandDetail from './pages/buyer/DemandDetail';
import BulkBrowse from './pages/buyer/BulkBrowse';
import BuyerAccount from './pages/buyer/BuyerAccount';
import BuyerReportIssue from './pages/buyer/ReportIssue';

// Vendor
import VendorDashboard from './pages/vendor/VendorDashboard';
import VendorOnboarding from './pages/vendor/VendorOnboarding';
import BrowseDemands from './pages/vendor/BrowseDemands';
import MyOffers from './pages/vendor/MyOffers';
import LeadFilters from './pages/vendor/LeadFilters';
import BulkManager from './pages/vendor/BulkManager';
import VendorAnalytics from './pages/vendor/VendorAnalytics';
import Wallet from './pages/vendor/Wallet';
import Invoices from './pages/vendor/Invoices';
import InvoiceDetail from './pages/vendor/InvoiceDetail';
import VendorKyc from './pages/vendor/Kyc';
import VendorAccount from './pages/vendor/Account';
import VendorReportIssue from './pages/vendor/ReportIssue';

// Admin
import AdminDashboard from './pages/admin/AdminDashboard';
import TaxonomyManager from './pages/admin/TaxonomyManager';
import LeadPricingManager from './pages/admin/LeadPricingManager';
import ItemManager from './pages/admin/ItemManager';
import VendorManagement from './pages/admin/VendorManagement';
import DemandModeration from './pages/admin/DemandModeration';
import WalletOverview from './pages/admin/WalletOverview';
import InvoiceManagement from './pages/admin/InvoiceManagement';
import AdminAnalytics from './pages/admin/AdminAnalytics';
import GeoAnalytics from './pages/admin/GeoAnalytics';
import MarketProfiles from './pages/admin/MarketProfiles';
import ProductDiscovery from './pages/admin/ProductDiscovery';
import CatalogImages from './pages/admin/CatalogImages';
import CatalogAttributes from './pages/admin/CatalogAttributes';
import AiSetup from './pages/admin/AiSetup';
import VendorDiscovery from './pages/admin/VendorDiscovery';
import VendorOutreach from './pages/admin/VendorOutreach';
import GstRules from './pages/admin/GstRules';
import VendorKycReview from './pages/admin/VendorKycReview';
import EmailTemplates from './pages/admin/EmailTemplates';
import SystemSettings from './pages/admin/SystemSettings';
import CreditRequests from './pages/admin/CreditRequests';
import FeedbackManager from './pages/admin/FeedbackManager';
import SystemIssues from './pages/admin/SystemIssues';
import AdminProfile from './pages/admin/AdminProfile';
import { DashboardSkeleton } from './components/skeletons';

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <DashboardSkeleton />;
  if (!user) return children;
  // If the user was bounced here with ?next=... (e.g. guest → fill bid →
  // submit → login), honour that destination instead of the role default.
  const params = new URLSearchParams(window.location.search);
  const next = params.get('next');
  if (next) return <Navigate to={next} replace />;
  if (user.role === 'vendor') return <Navigate to="/vendor/dashboard" replace />;
  if (user.role === 'admin') return <Navigate to="/admin/dashboard" replace />;
  return <Navigate to="/buyer/home" replace />;
}

function RoleRoute({ role, children }) {
  const { user, loading } = useAuth();
  if (loading) return <DashboardSkeleton />;
  if (!user) {
    if (role === 'vendor') return <Navigate to="/vendor/login" replace />;
    if (role === 'admin') return <Navigate to="/admin/login" replace />;
    return <Navigate to="/login" replace />;
  }
  if (user.role !== role) {
    if (user.role === 'vendor') return <Navigate to="/vendor/dashboard" replace />;
    if (user.role === 'admin') return <Navigate to="/admin/dashboard" replace />;
    return <Navigate to="/buyer/home" replace />;
  }
  return children;
}

/**
 * Renders a marketing sub-page inside the shared PublicLayout chrome, wires
 * a legacy-style `go(name)` helper to real-route navigation, and mounts a
 * Seo block with a per-page title/description/canonical so each URL is
 * independently indexable.
 */
function MarketingRoute({ Component, title, description, canonical }) {
  const navigate = useNavigate();
  const go = useCallback((name) => navigate(PAGE_TO_PATH[name] || '/'), [navigate]);
  return (
    <PublicLayout>
      <Seo title={title} description={description} canonical={canonical} />
      <MarketingFonts />
      <Component go={go} />
    </PublicLayout>
  );
}

// Single source of truth for the marketing route table. The sitemap.xml
// generator on the backend mirrors this set.
const MARKETING_ROUTES = [
  { path: '/buyers',        Component: BuyersPage,       title: 'For buyers — how reverse bidding works', description: "Post any demand at the price you want to pay. Verified vendors across India compete to win your order." },
  { path: '/vendors',       Component: VendorsPage,      title: 'For vendors — pay-per-matching-lead', description: 'Configure category, brand, and buyer price range. Only pay for leads that match your business.' },
  { path: '/pricing',       Component: PricingPage,      title: 'Pricing — transparent lead-fee rates', description: 'Transparent per-lead pricing for vendors on Kanjusee. Buyers always pay zero. Intra- and inter-state GST fully disclosed.' },
  { path: '/bulk-sales',    Component: BulkSalesPage,    title: 'Bulk sales — list inventory, let buyers bid', description: 'Vendors list inventory with a price range and stock quantity. Buyers bid within your range. Great for wholesale and clearance.' },
  { path: '/services',      Component: ServicesPage,     title: 'Services marketplace — from GST filing to tours', description: 'Post any service requirement; qualified verified providers send you their best quotes on Kanjusee.' },
  { path: '/blog',          Component: BlogPage,         title: 'Blog — guides, market insights, and how-tos', description: 'Articles on buyer strategy, vendor operations, and market trends on Kanjusee.' },
  { path: '/about',         Component: AboutPage,        title: "About Kanjusee — India's buyer-powered marketplace", description: "A venture by The Imaginations, Surat. India's first buyer-powered reverse-bidding marketplace." },
  { path: '/contact',       Component: ContactPage,      title: 'Contact Kanjusee', description: 'Reach the Kanjusee team in Surat, Gujarat for support, partnerships, or press enquiries.' },
  { path: '/vendor-signup', Component: VendorSignupPage, title: 'Vendor sign-up', description: 'Register as a vendor on Kanjusee in three steps. Free to join; pay only per matching lead.' },
];

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

export default function App() {
  return (
    <HelmetProvider>
    <AuthProvider>
    <NotificationProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          {/* Public */}
          <Route path="/" element={<Website />} />
          <Route path="/catalog" element={<Catalog />} />
          <Route path="/bulk" element={<BulkListings />} />
          <Route path="/item/:slug" element={<ItemDetail />} />
          <Route path="/c/:slug" element={<CategoryPage />} />
          <Route path="/recent-demands" element={<RecentDemands />} />
          {/* Public post-demand: guests can fill the form; auth is required only on submit */}
          <Route path="/post-demand" element={<PublicLayout><div style={{ background: 'var(--cream)', minHeight: 'calc(100vh - 64px)', padding: '32px 24px' }}><PostDemand /></div></PublicLayout>} />
          <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
          <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
          <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
          <Route path="/download" element={<AppDownload />} />

          {/* Dedicated vendor login — same auth endpoint as /login, but the
              UI + copy is tailored to vendors so marketing and nav links can
              point at a vendor-specific URL. The vendor-register aliases are
              thin redirects into /register with role pre-selected. */}
          <Route path="/vendor/login"    element={<PublicRoute><VendorLogin /></PublicRoute>} />
          <Route path="/vendor-login"    element={<PublicRoute><VendorLogin /></PublicRoute>} />
          <Route path="/vendor/register" element={<Navigate to="/register?role=vendor&next=/vendor/onboarding" replace />} />
          <Route path="/vendor-register" element={<Navigate to="/register?role=vendor&next=/vendor/onboarding" replace />} />
          <Route path="/admin/login"     element={<PublicRoute><AdminLogin /></PublicRoute>} />
          <Route path="/admin-login"     element={<PublicRoute><AdminLogin /></PublicRoute>} />

          {/* Marketing sub-pages — each at its own discoverable URL */}
          {MARKETING_ROUTES.map(m => (
            <Route
              key={m.path}
              path={m.path}
              element={<MarketingRoute Component={m.Component} title={m.title} description={m.description} canonical={m.path} />}
            />
          ))}

          {/* Legal pages */}
          <Route path="/terms" element={<LegalPage doc="terms" />} />
          <Route path="/terms-of-use" element={<Navigate to="/terms" replace />} />
          <Route path="/privacy" element={<LegalPage doc="privacy" />} />
          <Route path="/privacy-policy" element={<Navigate to="/privacy" replace />} />
          <Route path="/disclaimer" element={<LegalPage doc="disclaimer" />} />
          <Route path="/responsibilities" element={<LegalPage doc="responsibilities" />} />
          <Route path="/refund-policy" element={<LegalPage doc="refund" />} />
          <Route path="/payment-refund-policy" element={<Navigate to="/refund-policy" replace />} />
          <Route path="/grievance" element={<LegalPage doc="grievance" />} />
          <Route path="/grievance-redressal" element={<Navigate to="/grievance" replace />} />

          {/* Buyer */}
          <Route path="/buyer" element={<RoleRoute role="buyer"><AppLayout /></RoleRoute>}>
            <Route path="home" element={<BuyerHome />} />
            <Route path="post-demand" element={<PostDemand />} />
            <Route path="demands" element={<MyDemands />} />
            <Route path="demands/:id" element={<DemandDetail />} />
            <Route path="bulk" element={<BulkBrowse />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="account" element={<BuyerAccount />} />
            <Route path="messages" element={<Messages />} />
            <Route path="security" element={<Security />} />
            <Route path="issues" element={<BuyerReportIssue />} />
            <Route index element={<Navigate to="home" replace />} />
          </Route>

          {/* Vendor */}
          <Route path="/vendor" element={<RoleRoute role="vendor"><AppLayout /></RoleRoute>}>
            <Route path="dashboard" element={<VendorDashboard />} />
            <Route path="onboarding" element={<VendorOnboarding />} />
            <Route path="demands" element={<BrowseDemands />} />
            <Route path="offers" element={<MyOffers />} />
            <Route path="filters" element={<LeadFilters />} />
            <Route path="bulk" element={<BulkManager />} />
            <Route path="analytics" element={<VendorAnalytics />} />
            <Route path="wallet" element={<Wallet />} />
            <Route path="invoices" element={<Invoices />} />
            <Route path="invoices/:id" element={<InvoiceDetail />} />
            <Route path="kyc" element={<VendorKyc />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="account" element={<VendorAccount />} />
            <Route path="messages" element={<Messages />} />
            <Route path="issues" element={<VendorReportIssue />} />
            <Route index element={<Navigate to="dashboard" replace />} />
          </Route>

          {/* Admin */}
          <Route path="/admin" element={<RoleRoute role="admin"><AppLayout /></RoleRoute>}>
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="taxonomy" element={<TaxonomyManager />} />
            <Route path="pricing" element={<LeadPricingManager />} />
            <Route path="items" element={<ItemManager />} />
            <Route path="vendors" element={<VendorManagement />} />
            <Route path="kyc" element={<VendorKycReview />} />
            <Route path="demands" element={<DemandModeration />} />
            <Route path="wallets" element={<WalletOverview />} />
            <Route path="invoices" element={<InvoiceManagement />} />
            <Route path="gst-rules" element={<GstRules />} />
            <Route path="analytics" element={<AdminAnalytics />} />
            <Route path="geo-analytics" element={<GeoAnalytics />} />
            <Route path="market-profiles" element={<MarketProfiles />} />
            <Route path="discovery" element={<ProductDiscovery />} />
            <Route path="catalog-images" element={<CatalogImages />} />
            <Route path="catalog-attributes" element={<CatalogAttributes />} />
            <Route path="ai-setup" element={<AiSetup />} />
            <Route path="vendor-discovery" element={<VendorDiscovery />} />
            <Route path="vendor-outreach" element={<VendorOutreach />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="email-templates" element={<EmailTemplates />} />
            <Route path="settings" element={<SystemSettings />} />
            <Route path="credit-requests" element={<CreditRequests />} />
            <Route path="feedback" element={<FeedbackManager />} />
            <Route path="issues" element={<SystemIssues />} />
            <Route path="profile" element={<AdminProfile />} />
            <Route index element={<Navigate to="dashboard" replace />} />
          </Route>

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </NotificationProvider>
    </AuthProvider>
    </HelmetProvider>
  );
}
